import React from 'react';

type DecodedGpp = { sections: number[]; rawSegments: string[] } | null;

export function GppTcfHeader(props: {
  gpc: boolean | null;
  gppString: string | null;
  decoded: DecodedGpp;
  tcf: Record<string, unknown> | null;
}) {
  const { gpc, gppString, decoded, tcf } = props;
  return (
    <>
      <div className="rounded-lg border p-4 text-sm">
        <div>
          <b>GPC</b>: {gpc === null ? 'unknown' : gpc ? 'true' : 'false'}
        </div>
        <div className="mt-2">
          <b>GPP String</b>: <code className="break-all">{gppString ?? '(none yet)'}</code>
        </div>
        {decoded && (
          <div className="mt-2">
            <div>
              <b>Header sections</b>: [{decoded.sections.join(', ')}]
            </div>
            <div>
              <b>Segments</b>: {decoded.rawSegments.length}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4 text-sm">
        <h2 className="text-lg font-semibold mb-2">TCF v2.2 (Core)</h2>
        {tcf ? (
          <div className="space-y-1">
            <div>
              <b>GDPR applies</b>: {String(tcf['gdprApplies'])}
            </div>
            <div>
              <b>Policy</b>: v{String(tcf['tcfPolicyVersion'] ?? '')} &nbsp; <b>CMP</b>:{' '}
              {String(tcf['cmpId'] ?? '')}@{String(tcf['cmpVersion'] ?? '')}
            </div>
            <div>
              <b>Purposes (consent)</b>:{' '}
              {Object.entries(
                ((tcf['purpose'] as Record<string, unknown> | undefined)?.['consents'] ??
                  {}) as Record<string, boolean>,
              )
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '—'}
            </div>
            <div>
              <b>Purposes (LI)</b>:{' '}
              {Object.entries(
                ((tcf['purpose'] as Record<string, unknown> | undefined)?.['legitimateInterests'] ??
                  {}) as Record<string, boolean>,
              )
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '—'}
            </div>
            <div>
              <b>SF opt-ins</b>:{' '}
              {Object.entries((tcf['specialFeatureOptins'] ?? {}) as Record<string, boolean>)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '—'}
            </div>
            <div>
              <b>Vendors</b>: consent=
              {Number(
                (tcf['vendor'] as Record<string, unknown> | undefined)?.['consentsCount'] ?? 0,
              )}{' '}
              , li=
              {Number(
                (tcf['vendor'] as Record<string, unknown> | undefined)?.[
                  'legitimateInterestsCount'
                ] ?? 0,
              )}
            </div>
            {(tcf['tcString'] as string | undefined) && (
              <div className="mt-2">
                <b>TCString</b>: <code className="break-all">{String(tcf['tcString'])}</code>
              </div>
            )}
            {import.meta.env.MODE !== 'production' &&
              (tcf['devCoreString'] as string | undefined) && (
                <details className="mt-2">
                  <summary>devCoreString</summary>
                  <code className="break-all">{String(tcf['devCoreString'])}</code>
                </details>
              )}
          </div>
        ) : (
          <div className="text-sm text-gray-500">TCF disabled or no data yet.</div>
        )}
      </div>
      {tcf && (
        <div className="rounded-lg border p-4 text-sm">
          <h3 className="font-semibold mb-1">Publisher Purposes</h3>
          {(() => {
            const obj = tcf as Record<string, unknown>;
            const publisher = obj['publisher'] as Record<string, unknown> | undefined;
            const purposes = publisher?.['purposes'] as Record<string, unknown> | undefined;
            const cons = (purposes?.['consents'] as Record<string, boolean> | undefined) ?? {};
            const li =
              (purposes?.['legitimateInterests'] as Record<string, boolean> | undefined) ?? {};
            const cList =
              Object.entries(cons)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '—';
            const liList =
              Object.entries(li)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(', ') || '—';
            return (
              <>
                <div>Consent: {cList}</div>
                <div>LI: {liList}</div>
              </>
            );
          })()}
          {import.meta.env.MODE !== 'production' &&
            (() => {
              const obj = tcf as Record<string, unknown>;
              const s = obj['tcString'];
              return typeof s === 'string' && s.includes('.');
            })() && (
              <div className="text-xs text-gray-500 mt-1">
                Multi-segment TCString detected (Core.Publisher)
              </div>
            )}
        </div>
      )}
    </>
  );
}
